/**
 * batch-68 atv-tvt-uitleg (FO-344) - ATV en TVT in gewone taal.
 *
 * Eén pure helper voor alle uitleg rond de werktijd- en spaarinstellingen van
 * een medewerker: de drie zinnen op de portal-kaart (je-vorm), dezelfde matrix
 * in de derde persoon voor de backoffice, de rekenvoorbeelden van de keuzehulp,
 * het maximum per spaarpot en de vervaldatum. Portal, backoffice, uitlegpagina
 * en mutatieverzoek gebruiken deze functies; er komen geen losse teksten per
 * scherm (FO-344 §4.2).
 *
 * De rekenregels veranderen NIET. Wat er met een overuur of reisuur gebeurt,
 * komt altijd uit `tvtFactor` (FO-122, `payroll-calculations.ts`), dezelfde
 * bron als de saldo-opbouw (`triggerTVTFromWeekstaat`) en de geld-kant
 * (payroll: geld-factor `1,5 - werkFactor`). Daardoor kan een test de uitleg
 * en de echte opbouw tegen elkaar zetten (FO-344 acceptatiecriterium 7).
 *
 * Getallen staan nooit hard in de tekst: contracturen, ATV-uren, ATV-dagen,
 * maximum en vervaldatum komen uit de contractmutatie en het verlofbeleid
 * (`WerktijdSparenInvoerDTO`), zodat een parttimer zijn eigen getallen ziet.
 * De enige vaste getallen zijn de illustratie-hoeveelheden van de voorbeelden
 * (4 overuren, 5 uur reistijd) en de defaults van het Prisma-schema voor de
 * weergave zonder eigen gegevens.
 */

import {
  SPAAR_KEUZE_KORT_LABELS,
  SPAAR_KEUZE_LABELS,
  SPAARPOT_LABELS,
  WERKTIJD_SPAREN_LABELS,
  type HandleidingTokenWaardenDTO,
  type SpaarKeuze,
  type SpaarpotSoort,
  type VerlofOpbouwVlaggenDTO,
  type WerktijdSparenBeleidDTO,
  type WerktijdSparenBeschrijvingDTO,
  type WerktijdSparenInvoerDTO,
  type WerktijdSparenRekenvoorbeeldDTO,
  type WerktijdSparenSpaarpotDTO,
  type WerktijdSparenWaarschuwingDTO,
  type WerktijdSparenZinDTO,
} from '@streep/types'
import { tvtFactor } from './payroll-calculations'
import { berekenDaguren, round2 } from './leave-calculations'
import { berekenAtvUrenPerWeek, effectieveWerkUren } from './verlof-profiel'
import { vervalDatumVoorType } from './verlof-vervaldatum'
import { formatDagDatum, vandaagAmsterdam } from './dagtaken-datum'
import { tvtPlafond } from './tvt-plafond'

// ────────────────────────────────────────────────────────────────────────────
// Standaardwaarden (alleen voor weergave zonder eigen gegevens)
// ────────────────────────────────────────────────────────────────────────────

/**
 * De `@default`-waarden van `VerlofBeleid` in `schema.prisma` (CAO Metaal en
 * Techniek). Alleen gebruikt als er geen eigen beleid te laden is - de API
 * leest altijd het echte beleid van de medewerker.
 */
export const VERLOFBELEID_STANDAARD: WerktijdSparenBeleidDTO = {
  atvUrenPerJaar: 104,
  tvtWerkurenMaxWeken: 1,
  tvtReisurenMaxWeken: 1,
  tvtMaxOverdrachtWeken: 1,
  tvtNotificatieDrempel: 80,
}

/**
 * De standaard-medewerker voor de uitlegpagina zonder eigen gegevens: de
 * `@default`-waarden van `EmployeeContractMutation` (38u contract, 2u ATV per
 * week) met ATV-opbouw en "alles sparen" aan - de situatie waar de uitleg
 * over gaat.
 */
export const WERKTIJD_SPAREN_STANDAARD_INVOER: WerktijdSparenInvoerDTO = {
  contractUren: 38,
  werkUrenPerWeek: null,
  werkdagenPerWeek: 5,
  atvEnabled: true,
  atvHoursPerWeek: 2,
  tvtWerkurenEnabled: true,
  tvtReisurenEnabled: true,
  tvtWerkOpbouwPercentage: 100,
  tvtReisOpbouwPercentage: 100,
  beleid: VERLOFBELEID_STANDAARD,
}

/** Illustratie-hoeveelheden van de rekenvoorbeelden (FO-344 §3.2). */
export const VOORBEELD_OVERUREN = 4
export const VOORBEELD_REISTIJD = 5

/**
 * FAQ-tekst bij een volle spaarpot (FO-344 §11). Het maximum wordt (nog) niet
 * afgedwongen in de opbouw (FO-342 §11); zodra dat live is, wordt dit "wat
 * erboven komt, wordt uitbetaald". Eén tekstconstante, zodat die wissel op één
 * plek gebeurt.
 */
export const SPAARPOT_VOL_UITLEG =
  'Het maximum is een afspraak: HR neemt contact met je op als je pot vol is, zodat je vrije tijd kunt inplannen.'

// ────────────────────────────────────────────────────────────────────────────
// Opmaak
// ────────────────────────────────────────────────────────────────────────────

const urenFormat = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 })

/** Uren als NL-getal zonder overbodige decimalen: 40 -> "40", 12.5 -> "12,5". */
export function formatSpaarUren(uren: number): string {
  return urenFormat.format(round2(uren))
}

/** ISO-kalenderdatum (yyyy-mm-dd) -> "dd-mm-jjjj" zonder tijdzone-omrekening. */
export function formatKalenderdatum(iso: string): string {
  const [j, m, d] = iso.slice(0, 10).split('-')
  if (!j || !m || !d) return iso
  return `${d}-${m}-${j}`
}

/** Huidig kalenderjaar in Europe/Amsterdam (via de gedeelde `vandaagAmsterdam`). */
function huidigJaar(): number {
  return vandaagAmsterdam().getUTCFullYear()
}

// ────────────────────────────────────────────────────────────────────────────
// Keuzes <-> vlaggen (dezelfde payload als voorheen)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Wat er FEITELIJK met overuren of reistijd gebeurt, via `tvtFactor`: factor 0
 * = uitbetalen, 0,5 = half, 1 = alles sparen. Bij de legacy-combinatie "TVT aan
 * zonder ATV" is de factor 0, dus uitbetalen.
 */
export function feitelijkeSpaarKeuze(
  enabled: boolean,
  atvEnabled: boolean,
  percentage: number | null | undefined,
): SpaarKeuze {
  const factor = tvtFactor(enabled, atvEnabled, percentage)
  if (factor === 0) return 'UITBETALEN'
  return factor === 0.5 ? 'HALF' : 'ALLES'
}

/** Wat er INGESTELD staat (zonder de ATV-gate) - voor de legacy-detectie en HR. */
export function ingesteldeSpaarKeuze(
  enabled: boolean,
  percentage: number | null | undefined,
): SpaarKeuze {
  if (!enabled) return 'UITBETALEN'
  return Number(percentage) === 50 ? 'HALF' : 'ALLES'
}

/** Een keuze terug naar de schakelaar plus het percentage (FO-122). */
export function spaarKeuzeNaarVlag(keuze: SpaarKeuze): { enabled: boolean; percentage: 50 | 100 } {
  switch (keuze) {
    case 'UITBETALEN':
      return { enabled: false, percentage: 100 }
    case 'HALF':
      return { enabled: true, percentage: 50 }
    case 'ALLES':
      return { enabled: true, percentage: 100 }
  }
}

export interface OpbouwKeuze {
  atv: boolean
  overuren: SpaarKeuze
  reistijd: SpaarKeuze
}

/**
 * De payload van het bestaande `VERLOFOPBOUW`-mutatieverzoek, byte-gelijk aan
 * wat de oude schakelaars in de portal-modal verstuurden: zonder ATV gaat sparen
 * uit, en bij een uitgezette spaarsoort reist het percentage mee als 100.
 */
export function bouwVerlofOpbouwPayload(keuze: OpbouwKeuze): VerlofOpbouwVlaggenDTO {
  const werk = keuze.atv ? spaarKeuzeNaarVlag(keuze.overuren) : spaarKeuzeNaarVlag('UITBETALEN')
  const reis = keuze.atv ? spaarKeuzeNaarVlag(keuze.reistijd) : spaarKeuzeNaarVlag('UITBETALEN')
  return {
    atvOpbouw: keuze.atv,
    tvtWerk: werk.enabled,
    tvtReis: reis.enabled,
    tvtWerkPercentage: werk.enabled ? werk.percentage : 100,
    tvtReisPercentage: reis.enabled ? reis.percentage : 100,
  }
}

/**
 * Een (opgeslagen) VERLOFOPBOUW-payload terug naar keuzes. Leest defensief:
 * alleen 50 telt als half, en zonder ATV is sparen feitelijk uitbetalen.
 */
export function verlofOpbouwPayloadNaarKeuze(payload: {
  atvOpbouw?: unknown
  tvtWerk?: unknown
  tvtReis?: unknown
  tvtWerkPercentage?: unknown
  tvtReisPercentage?: unknown
}): OpbouwKeuze {
  const atv = payload.atvOpbouw === true
  return {
    atv,
    overuren: feitelijkeSpaarKeuze(payload.tvtWerk === true, atv, Number(payload.tvtWerkPercentage)),
    reistijd: feitelijkeSpaarKeuze(payload.tvtReis === true, atv, Number(payload.tvtReisPercentage)),
  }
}

/**
 * De opbouwvlaggen van een contract (snapshot of invoer) in de payload-vorm,
 * genormaliseerd zoals de API ze vergelijkt: het percentage telt alleen als die
 * spaarsoort aan staat (anders 100), en alleen 50 is "half".
 */
export function contractNaarOpbouwVlaggen(c: {
  atvEnabled: boolean
  tvtWerkurenEnabled: boolean
  tvtReisurenEnabled: boolean
  tvtWerkOpbouwPercentage: number | null | undefined
  tvtReisOpbouwPercentage: number | null | undefined
}): VerlofOpbouwVlaggenDTO {
  return {
    atvOpbouw: c.atvEnabled,
    tvtWerk: c.tvtWerkurenEnabled,
    tvtReis: c.tvtReisurenEnabled,
    tvtWerkPercentage: c.tvtWerkurenEnabled && Number(c.tvtWerkOpbouwPercentage) === 50 ? 50 : 100,
    tvtReisPercentage: c.tvtReisurenEnabled && Number(c.tvtReisOpbouwPercentage) === 50 ? 50 : 100,
  }
}

/** De huidige instellingen van een medewerker in de payload-vorm. */
export function invoerNaarVlaggen(invoer: WerktijdSparenInvoerDTO): VerlofOpbouwVlaggenDTO {
  return contractNaarOpbouwVlaggen(invoer)
}

/**
 * De werkuren-override na een goedgekeurd opbouwverzoek (#313): een override die
 * gelijk is aan de contracturen zegt niets ("werkt wat hij betaald krijgt") en
 * valt weg, zodat de werkweek weer wordt afgeleid (40 bij ATV op 38 uur); een
 * echte afwijking blijft staan. De ENE bron voor die regel: de contractmutatie
 * bij goedkeuring (`bouwVerlofOpbouwContractBody` in de API) en de uitleg van
 * het gevraagde resultaat (`invoerMetKeuze`, `invoerMetAtv`) gebruiken hem allebei.
 */
export function werkUrenNaOpbouwWijziging(contractUren: number, werkUrenPerWeek: number | null): number | null {
  return werkUrenPerWeek != null && werkUrenPerWeek > 0 && werkUrenPerWeek !== contractUren ? werkUrenPerWeek : null
}

/**
 * De no-change-regel van een opbouwverzoek: verschillen de vlaggen of de
 * effectieve percentages? Zo niet, dan komt er bij goedkeuring geen
 * contractmutatie. De ENE bron, gedeeld door de API
 * (`bouwVerlofOpbouwContractInput`) en de uitleg (`invoerMetKeuze`). Geef beide
 * kanten genormaliseerd mee (`contractNaarOpbouwVlaggen` / `bouwVerlofOpbouwPayload`).
 */
export function opbouwVlaggenGewijzigd(a: VerlofOpbouwVlaggenDTO, b: VerlofOpbouwVlaggenDTO): boolean {
  return (
    a.atvOpbouw !== b.atvOpbouw ||
    a.tvtWerk !== b.tvtWerk ||
    a.tvtReis !== b.tvtReis ||
    a.tvtWerkPercentage !== b.tvtWerkPercentage ||
    a.tvtReisPercentage !== b.tvtReisPercentage
  )
}

/**
 * Invoer met een (gevraagde) keuze erop, voor "Nieuw" in de keuzehulp en
 * "Gevraagd" bij HR: wat goedkeuren met een contractmutatie per vandaag
 * oplevert. Bij een wijziging (`opbouwVlaggenGewijzigd`) maakt de API die
 * mutatie met de #313-regel (`werkUrenNaOpbouwWijziging`); zonder wijziging
 * komt er geen mutatie en blijft de override staan. (Kiest HR een latere
 * ingangsdatum na een geplande mutatie, dan neemt de API de waarden van die
 * geplande mutatie over; zet HR "automatisch aanmaken" uit, dan komt er geen
 * mutatie. Die gevallen beschrijft de uitleg niet.)
 */
export function invoerMetKeuze(invoer: WerktijdSparenInvoerDTO, keuze: OpbouwKeuze): WerktijdSparenInvoerDTO {
  const vlaggen = bouwVerlofOpbouwPayload(keuze)
  const gewijzigd = opbouwVlaggenGewijzigd(vlaggen, invoerNaarVlaggen(invoer))
  return {
    ...invoer,
    werkUrenPerWeek: gewijzigd
      ? werkUrenNaOpbouwWijziging(invoer.contractUren, invoer.werkUrenPerWeek)
      : invoer.werkUrenPerWeek,
    atvEnabled: vlaggen.atvOpbouw,
    tvtWerkurenEnabled: vlaggen.tvtWerk,
    tvtReisurenEnabled: vlaggen.tvtReis,
    tvtWerkOpbouwPercentage: vlaggen.tvtWerkPercentage,
    tvtReisOpbouwPercentage: vlaggen.tvtReisPercentage,
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Getallen uit contract en beleid
// ────────────────────────────────────────────────────────────────────────────

/**
 * Werkuren per week - dezelfde regel als de overwerknorm van de weekstaat en
 * de ATV-opbouw (`effectieveWerkUren`, ATV-band-fix): een expliciete
 * `werkUrenPerWeek` wint, anders 40 bij 38 uur contract met ATV, anders de
 * contracturen. Een overuur is dus elk uur boven DEZE werkweek: 38 + ATV = 40,
 * zonder ATV 38, parttime 32 (ook met ATV aan, want dan levert ATV niets op).
 */
export function werkweekUren(invoer: WerktijdSparenInvoerDTO): number {
  return effectieveWerkUren(invoer.contractUren, invoer.werkUrenPerWeek, invoer.atvEnabled)
}

/**
 * ATV-uren per week die je spaart: de bestaande regel `berekenAtvUrenPerWeek`
 * (alleen bij 38 uur contract met een werkweek van 40 uur, dan 2; anders 0).
 */
function atvSpaarUrenPerWeek(invoer: WerktijdSparenInvoerDTO): number {
  return berekenAtvUrenPerWeek(invoer.contractUren, invoer.werkUrenPerWeek, invoer.atvEnabled)
}

/**
 * ATV-opbouw per jaar volgens dezelfde regel als de jaartoekenning
 * (`calculateEntitlementHours`): alleen bij de 38u -> 40u-combinatie, dan het
 * `atvUrenPerJaar` van het beleid (default 104), anders 0.
 */
export function atvUrenPerJaar(invoer: WerktijdSparenInvoerDTO): number {
  return atvSpaarUrenPerWeek(invoer) > 0 ? invoer.beleid.atvUrenPerJaar : 0
}

/**
 * Levert ATV vrije dagen op als de medewerker hem via een opbouwverzoek aanzet?
 * Alleen bij 38 uur contract met een werkweek van 40 uur (CAO, BR-2
 * `valideerAtvCombi`), gerekend met wat goedkeuren oplevert: een
 * werkuren-override gelijk aan de contracturen valt dan weg (#313), dus 38 uur
 * met een override van 38 kan wel; een echte afwijking (bv. 39) of een ander
 * contract (bv. 32 uur) niet.
 */
export function atvMogelijk(invoer: WerktijdSparenInvoerDTO): boolean {
  return atvSpaarUrenPerWeek(invoerMetAtv(invoer)) > 0
}

/**
 * Staat ATV aan zonder ATV-dagen op te leveren? Bij een parttimer, of bij 38 uur
 * met een werkuren-override die geen 40-urenweek geeft (de "gepinde" groep).
 * Eén predicaat voor de HR-melding, de ja-kaart van de keuzehulp en de banner
 * van de uitlegpagina.
 */
export function atvAanZonderOpbouw(invoer: WerktijdSparenInvoerDTO): boolean {
  return invoer.atvEnabled && atvSpaarUrenPerWeek(invoer) === 0
}

/**
 * Mag de keuzehulp "ja, ATV" laten kiezen? Als ATV iets oplevert, of als ATV nu
 * al aan staat (zoals HR het heeft ingesteld, bv. bij een parttimer die spaart):
 * die instelling blijft kiesbaar, zodat een andere wijziging (bijvoorbeeld de
 * reistijd) het sparen niet stilzwijgend uitzet. Een nieuwe ATV-keuze die niets
 * oplevert biedt de keuzehulp niet aan (PO-besluit over parttimers open, zie
 * STATUS van batch-68).
 */
export function atvKiesbaar(invoer: WerktijdSparenInvoerDTO): boolean {
  return atvMogelijk(invoer) || invoer.atvEnabled
}

/** Uren per werkdag via de gedeelde `berekenDaguren` (40u / 5 dagen = 8u). */
export function werktijdDaguren(invoer: WerktijdSparenInvoerDTO): number {
  return berekenDaguren(werkweekUren(invoer), invoer.werkdagenPerWeek > 0 ? invoer.werkdagenPerWeek : 5)
}

/** ATV-dagen per jaar: 104 uur / 8 uur per dag = 13 dagen (op 1 decimaal). */
export function atvDagenPerJaar(invoer: WerktijdSparenInvoerDTO): number {
  const daguren = werktijdDaguren(invoer)
  if (daguren <= 0) return 0
  return Math.round((atvUrenPerJaar(invoer) / daguren) * 10) / 10
}

/**
 * Maximum per spaarpot (FO-342 §3.6, FO-344 §2): `tvt*MaxWeken x één werkweek`,
 * 40 uur als ATV echt opbouwt (38 + 2), anders 38; bij parttime de eigen
 * contracturen (ook als ATV daar ten onrechte aan staat). Alleen een afspraak:
 * het maximum wordt (nog) niet afgedwongen.
 *
 * Eén bron met de plafondsignalering van de verloningscontrole: `tvtPlafond()`
 * in `tvt-plafond.ts` (batch-68 integratie, ADDENDA gedeelde bestanden 8).
 */
export function maxSparenPerPot(invoer: WerktijdSparenInvoerDTO, soort: 'TVT_WERK' | 'TVT_REIS'): number {
  return tvtPlafond({
    maxWeken: soort === 'TVT_WERK' ? invoer.beleid.tvtWerkurenMaxWeken : invoer.beleid.tvtReisurenMaxWeken,
    contractUrenPerWeek: invoer.contractUren,
    atvEnabled: invoer.atvEnabled,
    werkUrenPerWeek: invoer.werkUrenPerWeek,
  })
}

/**
 * Overdracht naar volgend jaar per spaarpot: zelfde regel als de TVT-vervalcron
 * (`getMaxOverdrachtUren`): contracturen x `tvtMaxOverdrachtWeken`.
 */
export function maxOverdrachtUren(invoer: WerktijdSparenInvoerDTO): number {
  return round2(Math.max(0, invoer.contractUren) * Math.max(0, invoer.beleid.tvtMaxOverdrachtWeken))
}

/**
 * Vervaldatum (yyyy-mm-dd) van de spaarpotten van opbouwjaar `jaar`, uit
 * dezelfde bron als het echte verval (`vervalDatumVoorType`): 31 december,
 * voor ATV en voor gespaarde overuren en reistijd (TVT) gelijk. De opbouw
 * (`uren.ts`) en de vervalcrons zetten die datum vast; het beleidsveld
 * `tvtVervalMaandDag` heeft daar geen effect op en wordt hier dus niet getoond.
 */
export function spaarpotVervaldatum(jaar: number): string {
  return formatDagDatum(vervalDatumVoorType('TVT_WERK', jaar))
}

/**
 * Verval van ATV in gewone taal: ATV kent geen overdracht, wat op de
 * vervaldatum over is vervalt helemaal (`verlof-entitlement-verval.ts`).
 * `JE` voor de medewerker (portal), `HR` voor de backoffice.
 */
export function vervalUitlegAtv(vervaldatumIso: string, vorm: 'JE' | 'HR' = 'JE'): string {
  const datum = formatKalenderdatum(vervaldatumIso)
  return vorm === 'JE'
    ? `ATV vervalt helemaal op ${datum}: plan je ATV-dagen binnen het jaar.`
    : `ATV vervalt helemaal op ${datum} (geen overdracht naar volgend jaar).`
}

/**
 * Verval van gespaarde overuren en reistijd (TVT): wat over is vervalt, behalve
 * het maximum dat per spaarpot doorschuift naar volgend jaar (`tvt-verval.ts`).
 */
export function vervalUitlegTvt(vervaldatumIso: string, maxOverdracht: number): string {
  const datum = formatKalenderdatum(vervaldatumIso)
  return maxOverdracht > 0
    ? `Gespaarde overuren en reistijd (TVT) vervallen op ${datum}. Maximaal ${formatSpaarUren(maxOverdracht)} uur per spaarpot schuift door naar volgend jaar; de rest vervalt.`
    : `Gespaarde overuren en reistijd (TVT) vervallen op ${datum}. Er schuift niets door naar volgend jaar.`
}

/** Voortgang van een spaarpot naar het maximum; oranje vanaf de beleidsdrempel (default 80). */
export function spaarpotVoortgang(
  saldo: number,
  max: number | null,
  drempelPct: number,
): { pct: number | null; bijnaVol: boolean; vol: boolean } {
  if (max == null || max <= 0) return { pct: null, bijnaVol: false, vol: false }
  const pct = Math.max(0, Math.round((saldo / max) * 100))
  return { pct, bijnaVol: pct >= drempelPct, vol: saldo >= max }
}

/** Eén spaarpot-DTO met label, maximum en voortgang. */
export function bouwSpaarpot(
  soort: SpaarpotSoort,
  saldo: number,
  invoer: WerktijdSparenInvoerDTO,
): WerktijdSparenSpaarpotDTO {
  const max = soort === 'ATV' ? null : maxSparenPerPot(invoer, soort)
  const voortgang = spaarpotVoortgang(saldo, max, invoer.beleid.tvtNotificatieDrempel)
  return { soort, label: SPAARPOT_LABELS[soort], saldo: round2(saldo), max, ...voortgang }
}

// ────────────────────────────────────────────────────────────────────────────
// De zinnen-matrix (FO-344 §4.2) - de enige bron van de teksten
// ────────────────────────────────────────────────────────────────────────────

const ZONDER_ATV = 'Sparen kan alleen met ATV.'

function werktijdZin(invoer: WerktijdSparenInvoerDTO): { zin: WerktijdSparenZinDTO; hr: string } {
  const week = formatSpaarUren(werkweekUren(invoer))
  const contract = formatSpaarUren(invoer.contractUren)
  const atvWeek = atvSpaarUrenPerWeek(invoer)
  if (atvWeek > 0) {
    const dagen = formatSpaarUren(atvDagenPerJaar(invoer))
    return {
      zin: {
        titel: `Je werkt ${week} uur per week.`,
        toelichting: `${contract} uur contract + ${formatSpaarUren(atvWeek)} uur die je spaart als ATV (${dagen} vrije dagen per jaar).`,
        status: WERKTIJD_SPAREN_LABELS.atv.kort,
      },
      hr: `Medewerker werkt ${week} uur en spaart ${formatSpaarUren(atvWeek)} uur per week als ATV (${dagen} dagen per jaar)`,
    }
  }
  if (invoer.atvEnabled) {
    // ATV staat aan, maar levert bij dit contract niets op (geen 38u -> 40u):
    // de werkweek is gewoon de contracturen. Eerlijk zeggen, HR krijgt een melding.
    return {
      zin: {
        titel: `Je werkt ${week} uur per week.`,
        toelichting: `${contract} uur contract. ATV staat aan, maar bij jouw contract levert dat geen ATV-dagen op; vraag HR om uitleg.`,
        status: 'Geen ATV-dagen',
      },
      hr: `Medewerker werkt ${week} uur met ATV aan, maar het contract levert geen ATV-dagen op`,
    }
  }
  return {
    zin: {
      titel: `Je werkt ${week} uur per week.`,
      toelichting: `${contract} uur contract. Je spaart geen ATV-dagen.`,
      status: 'Geen ATV',
    },
    hr: `Medewerker werkt ${week} uur, zonder ATV`,
  }
}

function overurenZin(keuze: SpaarKeuze, atv: boolean): { zin: WerktijdSparenZinDTO; hr: string } {
  const status = SPAAR_KEUZE_KORT_LABELS[keuze]
  switch (keuze) {
    case 'UITBETALEN':
      return {
        zin: {
          titel: 'Overuren krijg je uitbetaald.',
          toelichting: `Met overwerktoeslag, bij je salaris van de volgende maand.${atv ? '' : ` ${ZONDER_ATV}`}`,
          status,
        },
        hr: 'Medewerker krijgt overuren uitbetaald',
      }
    case 'HALF':
      return {
        zin: {
          titel: 'Overuren spaar je voor de helft op als vrije tijd.',
          toelichting:
            'De andere helft krijg je uitbetaald. De overwerktoeslag krijg je over al je overuren, bij je salaris van de volgende maand.',
          status,
        },
        hr: 'Medewerker spaart overuren half op, half uitbetaald',
      }
    case 'ALLES':
      return {
        zin: {
          titel: 'Overuren spaar je op als vrije tijd.',
          toelichting: 'Alleen de overwerktoeslag krijg je uitbetaald, bij je salaris van de volgende maand.',
          status,
        },
        hr: 'Medewerker spaart alle overuren op; alleen de toeslag wordt uitbetaald',
      }
  }
}

function reistijdZin(keuze: SpaarKeuze, atv: boolean): { zin: WerktijdSparenZinDTO; hr: string } {
  const status = SPAAR_KEUZE_KORT_LABELS[keuze]
  switch (keuze) {
    case 'UITBETALEN':
      return {
        zin: {
          titel: 'Reistijd krijg je uitbetaald.',
          toelichting: `Bij je salaris van de volgende maand.${atv ? '' : ` ${ZONDER_ATV}`}`,
          status,
        },
        hr: 'Medewerker krijgt reistijd uitbetaald',
      }
    case 'HALF':
      return {
        zin: {
          titel: 'Reistijd spaar je voor de helft op als vrije tijd.',
          toelichting: 'De andere helft krijg je uitbetaald, bij je salaris van de volgende maand.',
          status,
        },
        hr: 'Medewerker spaart reistijd half op, half uitbetaald',
      }
    case 'ALLES':
      return {
        zin: {
          titel: 'Reistijd spaar je op als vrije tijd.',
          toelichting: 'Je uitbetaalbare reistijd gaat naar je spaarpot.',
          status,
        },
        hr: 'Medewerker spaart alle reistijd op',
      }
  }
}

/** De HR-melding bij de legacy-combinatie (FO-344 §4.3). */
export const TVT_ZONDER_ATV_HR_TEKST = 'Instelling ongeldig: TVT aan zonder ATV - heeft geen effect'

/**
 * De drie zinnen, de HR-zinnen, de feitelijke keuzes, de getallen en de
 * waarschuwingen voor één set instellingen. Alle tien geldige combinaties
 * (ATV uit, of ATV aan met overuren x reistijd in uitbetalen/half/alles)
 * leveren eigen zinnen; de legacy-combinatie "TVT aan zonder ATV" toont wat er
 * feitelijk gebeurt (uitbetalen) plus een melding voor HR.
 *
 * `peiljaar` bepaalt het jaar van de vervaldatum (default: het huidige jaar in
 * Europe/Amsterdam).
 */
export function beschrijfWerktijdSparen(
  invoer: WerktijdSparenInvoerDTO,
  opties: { peiljaar?: number } = {},
): WerktijdSparenBeschrijvingDTO {
  const atv = invoer.atvEnabled
  const overurenKeuze = feitelijkeSpaarKeuze(invoer.tvtWerkurenEnabled, atv, invoer.tvtWerkOpbouwPercentage)
  const reistijdKeuze = feitelijkeSpaarKeuze(invoer.tvtReisurenEnabled, atv, invoer.tvtReisOpbouwPercentage)

  const werktijd = werktijdZin(invoer)
  const overuren = overurenZin(overurenKeuze, atv)
  const reistijd = reistijdZin(reistijdKeuze, atv)

  const waarschuwingen: WerktijdSparenWaarschuwingDTO[] = []
  if (!atv && (invoer.tvtWerkurenEnabled || invoer.tvtReisurenEnabled)) {
    waarschuwingen.push({ code: 'TVT_ZONDER_ATV', doelgroep: 'HR', tekst: TVT_ZONDER_ATV_HR_TEKST })
  }
  if (atvAanZonderOpbouw(invoer)) {
    waarschuwingen.push({
      code: 'ATV_ZONDER_OPBOUW',
      doelgroep: 'HR',
      tekst: `ATV staat aan, maar bij ${formatSpaarUren(invoer.contractUren)} uur contract en ${formatSpaarUren(werkweekUren(invoer))} uur werken bouwt de medewerker geen ATV-dagen op (alleen bij 38 uur contract met 40 uur werken)`,
    })
  }

  const jaar = opties.peiljaar ?? huidigJaar()
  return {
    zinnen: { werktijd: werktijd.zin, overuren: overuren.zin, reistijd: reistijd.zin },
    hrZinnen: { werktijd: werktijd.hr, overuren: overuren.hr, reistijd: reistijd.hr },
    keuzes: { atv, overuren: overurenKeuze, reistijd: reistijdKeuze },
    getallen: {
      contractUren: invoer.contractUren,
      werkweekUren: werkweekUren(invoer),
      atvUrenPerWeek: atvSpaarUrenPerWeek(invoer),
      atvUrenPerJaar: atvUrenPerJaar(invoer),
      atvDagenPerJaar: atvDagenPerJaar(invoer),
      daguren: werktijdDaguren(invoer),
      maxSparenOveruren: maxSparenPerPot(invoer, 'TVT_WERK'),
      maxSparenReistijd: maxSparenPerPot(invoer, 'TVT_REIS'),
      maxOverdrachtUren: maxOverdrachtUren(invoer),
      vervaldatum: spaarpotVervaldatum(jaar),
      drempelPct: invoer.beleid.tvtNotificatieDrempel,
    },
    waarschuwingen,
  }
}

/**
 * Een wijziging in gewone taal, bijvoorbeeld "overuren half sparen, half
 * uitbetalen". Alleen wat verschilt; "geen wijziging" als alles gelijk is.
 */
export function vatOpbouwWijzigingSamen(huidig: OpbouwKeuze, gevraagd: OpbouwKeuze): string {
  const delen: string[] = []
  if (huidig.atv !== gevraagd.atv) {
    delen.push(gevraagd.atv ? WERKTIJD_SPAREN_LABELS.atv.label : 'geen ATV meer')
  }
  if (huidig.overuren !== gevraagd.overuren) {
    delen.push(`overuren ${SPAAR_KEUZE_LABELS[gevraagd.overuren].toLowerCase()}`)
  }
  if (huidig.reistijd !== gevraagd.reistijd) {
    delen.push(`reistijd ${SPAAR_KEUZE_LABELS[gevraagd.reistijd].toLowerCase()}`)
  }
  return delen.length > 0 ? delen.join(', ') : 'geen wijziging'
}

// ────────────────────────────────────────────────────────────────────────────
// Rekenvoorbeelden (FO-344 §3.2, acceptatiecriterium 7)
// ────────────────────────────────────────────────────────────────────────────

export type RekenScenario =
  /** Een normale week: werken -> ATV-opbouw. */
  | { soort: 'WEEK' }
  /** Overuren deze week; zonder `keuze` geldt de huidige instelling. */
  | { soort: 'OVERUREN'; uren?: number; keuze?: SpaarKeuze }
  /** Uitbetaalbare reistijd deze maand; zonder `keuze` geldt de huidige instelling. */
  | { soort: 'REISTIJD'; uren?: number; keuze?: SpaarKeuze }

/**
 * Rekenvoorbeeld met de eigen getallen. De verdeling sparen/uitbetalen komt uit
 * `tvtFactor`, exact zoals de opbouw bij de weekstaat (`uren x factor`, op 2
 * decimalen) en de geld-kant van payroll (overuren: basis over de niet-gespaarde
 * uren plus de toeslag over alle overuren, oftewel geld-factor `1,5 - factor`;
 * reistijd: `1 - factor`, zonder toeslag).
 */
export function rekenvoorbeeld(
  invoer: WerktijdSparenInvoerDTO,
  scenario: RekenScenario,
): WerktijdSparenRekenvoorbeeldDTO {
  if (scenario.soort === 'WEEK') {
    const week = werkweekUren(invoer)
    const atvWeek = atvSpaarUrenPerWeek(invoer)
    return {
      uitbetaaldUren: round2(week - atvWeek),
      gespaardUren: atvWeek,
      toeslagOverUren: 0,
      tekst:
        atvWeek > 0
          ? `Een normale week: ${formatSpaarUren(week)} uur werken → ${formatSpaarUren(atvWeek)} uur in je ATV-pot.`
          : `Een normale week: ${formatSpaarUren(week)} uur werken → niets gespaard.`,
    }
  }

  const isOveruren = scenario.soort === 'OVERUREN'
  const uren = Math.max(0, scenario.uren ?? (isOveruren ? VOORBEELD_OVERUREN : VOORBEELD_REISTIJD))
  const vlag = scenario.keuze
    ? spaarKeuzeNaarVlag(scenario.keuze)
    : isOveruren
      ? { enabled: invoer.tvtWerkurenEnabled, percentage: invoer.tvtWerkOpbouwPercentage }
      : { enabled: invoer.tvtReisurenEnabled, percentage: invoer.tvtReisOpbouwPercentage }
  const factor = tvtFactor(vlag.enabled, invoer.atvEnabled, vlag.percentage)
  const gespaard = round2(uren * factor)
  const uitbetaald = round2(uren - gespaard)
  const u = formatSpaarUren

  if (isOveruren) {
    const tekst =
      factor === 0
        ? `${u(uren)} u + toeslag uitbetaald · 0 u gespaard`
        : uitbetaald > 0
          ? `${u(uitbetaald)} u (+ toeslag over ${u(uren)} u) uitbetaald · ${u(gespaard)} u gespaard`
          : `Toeslag over ${u(uren)} u uitbetaald · ${u(gespaard)} u gespaard`
    return { uitbetaaldUren: uitbetaald, gespaardUren: gespaard, toeslagOverUren: uren, tekst }
  }

  const tekst =
    uitbetaald > 0
      ? `${u(uitbetaald)} u uitbetaald · ${u(gespaard)} u gespaard`
      : `Niets uitbetaald · ${u(gespaard)} u gespaard`
  return { uitbetaaldUren: uitbetaald, gespaardUren: gespaard, toeslagOverUren: 0, tekst }
}

// ────────────────────────────────────────────────────────────────────────────
// Keuzehulp (FO-344 §3.2): vragen en keuzekaarten met eigen rekenvoorbeeld
// ────────────────────────────────────────────────────────────────────────────

export const KEUZEHULP_STAPPEN = ['ATV', 'OVERUREN', 'REISTIJD', 'SAMENVATTING'] as const
export type KeuzehulpStap = (typeof KEUZEHULP_STAPPEN)[number]

export const KEUZEHULP_VRAGEN: Record<KeuzehulpStap, { vraag: string; voorbeeld: string | null }> = {
  ATV: { vraag: 'Wil je ATV opbouwen?', voorbeeld: null },
  OVERUREN: {
    vraag: 'Wat wil je met je overuren?',
    voorbeeld: `Voorbeeld: ${VOORBEELD_OVERUREN} overuren deze week.`,
  },
  REISTIJD: {
    vraag: 'Wat wil je met je reistijd?',
    voorbeeld: `Voorbeeld: ${VOORBEELD_REISTIJD} uur uitbetaalbare reistijd deze maand.`,
  },
  SAMENVATTING: { vraag: 'Zo wordt het', voorbeeld: null },
}

export interface KeuzeKaart<T> {
  waarde: T
  titel: string
  uitleg: string
  /** Rekenvoorbeeld met de eigen getallen (via `rekenvoorbeeld`). */
  voorbeeld: string
  /**
   * Gezet als deze keuze bij het eigen contract niet kan: de reden, in gewone
   * taal. De kaart staat dan uit (niet te kiezen) en toont de reden.
   */
  uitgeschakeld?: string
}

const SPAAR_KAART_UITLEG: Record<'OVERUREN' | 'REISTIJD', Record<SpaarKeuze, string>> = {
  OVERUREN: {
    UITBETALEN: 'Je krijgt ze betaald met toeslag, bij je salaris van de volgende maand.',
    HALF: 'De helft gaat naar je spaarpot, de andere helft krijg je betaald.',
    ALLES: 'Alle overuren gaan naar je spaarpot. Je krijgt alleen de toeslag uitbetaald.',
  },
  REISTIJD: {
    UITBETALEN: 'Je krijgt je reistijd betaald, bij je salaris van de volgende maand.',
    HALF: 'De helft gaat naar je spaarpot, de andere helft krijg je betaald.',
    ALLES: 'Al je uitbetaalbare reistijd gaat naar je spaarpot.',
  },
}

/**
 * Invoer met ATV aan, zoals een opbouwverzoek met ATV dat zou opleveren (met de
 * #313-regel voor de werkuren): voor de ja-kaart, de spaarvragen (die alleen met
 * ATV bestaan) en de ATV-tokens van de uitlegpagina.
 */
function invoerMetAtv(invoer: WerktijdSparenInvoerDTO): WerktijdSparenInvoerDTO {
  return {
    ...invoer,
    werkUrenPerWeek: werkUrenNaOpbouwWijziging(invoer.contractUren, invoer.werkUrenPerWeek),
    atvEnabled: true,
    atvHoursPerWeek:
      invoer.atvHoursPerWeek > 0 ? invoer.atvHoursPerWeek : WERKTIJD_SPAREN_STANDAARD_INVOER.atvHoursPerWeek,
  }
}

/**
 * Stap 1: "Wil je ATV opbouwen?" - ja/nee met de eigen getallen. Drie gevallen
 * voor de ja-kaart:
 *   - ATV staat nu aan maar levert niets op (`atvAanZonderOpbouw`: parttimer, of
 *     de gepinde groep met 38 uur en een werkuren-override van 38): "zoals nu",
 *     kiesbaar, met de reden (contract of werkweek). Bij de gepinde groep staat
 *     erbij dat een andere wijziging de werkweek bij goedkeuren op 40 zet (de
 *     #313-regel), want alleen "ja" kiezen maakt geen mutatie;
 *   - ATV levert iets op als hij aangaat (`atvMogelijk`): de normale kaart;
 *   - anders staat de ja-kaart uit met de reden: zo vraagt niemand via de
 *     portal een nieuwe combinatie aan die de CAO niet kent.
 */
export function atvKeuzeKaarten(invoer: WerktijdSparenInvoerDTO): KeuzeKaart<boolean>[] {
  const metAtv = invoerMetAtv(invoer)
  const zonderAtv = invoerMetKeuze(invoer, { atv: false, overuren: 'UITBETALEN', reistijd: 'UITBETALEN' })
  const u = formatSpaarUren
  // ATV staat nu aan maar levert niets op: ook bij de "gepinde" groep (38 uur met
  // een werkuren-override van 38) belooft "ja" zonder andere wijziging niets,
  // want zonder wijziging komt er geen contractmutatie.
  const zonderOpbouw = atvAanZonderOpbouw(invoer)
  // Gepind: met een wijziging valt de override weg en bouwt ATV wel op.
  const herstelt = zonderOpbouw && atvMogelijk(invoer)
  const reden =
    invoer.contractUren === 38
      ? `In jouw contract staat een werkweek van ${u(werkweekUren(invoer))} uur; daardoor levert ATV nu geen vrije dagen op.`
      : `Bij jouw contract van ${u(invoer.contractUren)} uur levert ATV geen vrije dagen op.`
  const ja: KeuzeKaart<boolean> = zonderOpbouw
    ? {
        waarde: true,
        titel: 'Ja, ATV blijft aan (zoals nu)',
        uitleg: `${reden} ATV staat nu aan; daardoor kun je wel overuren en reistijd sparen.${
          herstelt
            ? ` Vraag je ook iets anders aan, dan wordt je werkweek bij goedkeuren ${u(werkweekUren(metAtv))} uur en bouw je wel ATV-dagen op.`
            : ''
        } Vragen hierover? Overleg met HR.`,
        voorbeeld: rekenvoorbeeld(invoer, { soort: 'WEEK' }).tekst,
      }
    : atvMogelijk(invoer)
      ? {
          waarde: true,
          titel: `Ja, ik werk ${u(werkweekUren(metAtv))} uur en spaar ${u(atvSpaarUrenPerWeek(metAtv))} uur per week als vrije dagen`,
          uitleg: `Je krijgt ${u(atvDagenPerJaar(metAtv))} vrije dagen per jaar extra. Je kunt dan ook overuren en reistijd sparen.`,
          voorbeeld: rekenvoorbeeld(metAtv, { soort: 'WEEK' }).tekst,
        }
      : {
          waarde: true,
          titel: 'Ja, ik wil ATV-dagen opbouwen',
          uitleg: 'ATV hoort bij een contract van 38 uur met een 40-urenweek. Daarbij hoort ook het sparen van overuren en reistijd.',
          voorbeeld: '',
          uitgeschakeld:
            invoer.contractUren === 38
              ? `Kan niet: in jouw contract staat een werkweek van ${u(werkweekUren(zonderAtv))} uur. Vraag HR als je hier vragen over hebt.`
              : `Kan niet bij jouw contract van ${u(invoer.contractUren)} uur. Vraag HR als je hier vragen over hebt.`,
        }
  const werkweekZonderAtv = werkweekUren(zonderAtv)
  const nee: KeuzeKaart<boolean> = {
    waarde: false,
    titel:
      werkweekZonderAtv === invoer.contractUren
        ? `Nee, ik werk mijn ${u(invoer.contractUren)} contracturen`
        : `Nee, ik werk ${u(werkweekZonderAtv)} uur zonder ATV`,
    uitleg: 'Geen extra vrije dagen. Overuren en reistijd worden altijd uitbetaald.',
    voorbeeld: rekenvoorbeeld(zonderAtv, { soort: 'WEEK' }).tekst,
  }
  return [ja, nee]
}

/** Stap 2 en 3: uitbetalen, half of alles sparen - met het voorbeeld via `tvtFactor`. */
export function spaarKeuzeKaarten(
  invoer: WerktijdSparenInvoerDTO,
  soort: 'OVERUREN' | 'REISTIJD',
): KeuzeKaart<SpaarKeuze>[] {
  const metAtv = invoerMetAtv(invoer)
  return (['UITBETALEN', 'HALF', 'ALLES'] as const).map((keuze) => ({
    waarde: keuze,
    titel: SPAAR_KEUZE_LABELS[keuze],
    uitleg: SPAAR_KAART_UITLEG[soort][keuze],
    voorbeeld: rekenvoorbeeld(metAtv, { soort, keuze }).tekst,
  }))
}

/** De volgende stap: bij ATV "nee" worden de spaarvragen overgeslagen. */
export function volgendeKeuzehulpStap(stap: KeuzehulpStap, keuze: OpbouwKeuze): KeuzehulpStap {
  switch (stap) {
    case 'ATV':
      return keuze.atv ? 'OVERUREN' : 'SAMENVATTING'
    case 'OVERUREN':
      return 'REISTIJD'
    case 'REISTIJD':
    case 'SAMENVATTING':
      return 'SAMENVATTING'
  }
}

/** De vorige stap, of `null` bij de eerste. */
export function vorigeKeuzehulpStap(stap: KeuzehulpStap, keuze: OpbouwKeuze): KeuzehulpStap | null {
  switch (stap) {
    case 'ATV':
      return null
    case 'OVERUREN':
      return 'ATV'
    case 'REISTIJD':
      return 'OVERUREN'
    case 'SAMENVATTING':
      return keuze.atv ? 'REISTIJD' : 'ATV'
  }
}

/** Zijn stap 2 en 3 overgeslagen? (ATV "nee": sparen kan alleen met ATV.) */
export function keuzehulpSlaatSparenOver(keuze: OpbouwKeuze): boolean {
  return !keuze.atv
}

/** De uitleg bij overgeslagen spaarvragen (FO-344 §3.2, samenvatting). */
export const SPAREN_ALLEEN_MET_ATV =
  'Overuren en reistijd: uitbetaald (sparen kan alleen met ATV).'

// ────────────────────────────────────────────────────────────────────────────
// Placeholders voor de uitlegpagina (handleiding atv-tvt-uitleg.md)
// ────────────────────────────────────────────────────────────────────────────

/** "één werkweek", "2 werkweken" of, als overuren en reistijd verschillen, beide. */
function maxSparenOmvang(beleid: WerktijdSparenInvoerDTO['beleid']): string {
  const weken = (n: number) => (n === 1 ? 'één werkweek' : `${formatSpaarUren(n)} werkweken`)
  return beleid.tvtWerkurenMaxWeken === beleid.tvtReisurenMaxWeken
    ? weken(beleid.tvtWerkurenMaxWeken)
    : `${weken(beleid.tvtWerkurenMaxWeken)} (overuren) en ${weken(beleid.tvtReisurenMaxWeken)} (reistijd)`
}

/**
 * De tokenwaarden voor de handleiding. Zonder eigen gegevens (geen sessie, geen
 * medewerker of nog geen contract) rekent de pagina met de standaard-invoer.
 */
export function handleidingTokens(
  invoer: WerktijdSparenInvoerDTO = WERKTIJD_SPAREN_STANDAARD_INVOER,
  opties: { peiljaar?: number; vervaldatum?: string } = {},
): HandleidingTokenWaardenDTO {
  // De uitleg gaat over sparen mét ATV: ook wie nu geen ATV opbouwt, ziet wat
  // ATV met het eigen contract oplevert. Levert het eigen contract geen ATV op
  // (ATV bestaat alleen als 38u -> 40u), dan beschrijven de ATV-tokens de
  // standaardregeling met het eigen beleid; contracturen blijven de eigen.
  const atvBron: WerktijdSparenInvoerDTO = atvMogelijk(invoer)
    ? invoerMetAtv(invoer)
    : { ...WERKTIJD_SPAREN_STANDAARD_INVOER, beleid: invoer.beleid }
  const vervaldatum = opties.vervaldatum ?? spaarpotVervaldatum(opties.peiljaar ?? huidigJaar())
  const maxOverdracht = maxOverdrachtUren(invoer)
  const zonderAtv = invoerMetKeuze(invoer, { atv: false, overuren: 'UITBETALEN', reistijd: 'UITBETALEN' })
  const nuZonderOpbouw = atvAanZonderOpbouw(invoer)
  // "Met ATV": wie ATV al aan heeft, houdt zonder wijziging de huidige instelling
  // (alleen "ja" kiezen maakt geen mutatie, ook niet bij de gepinde groep); voor
  // de rest wat een ATV-aanvraag oplevert. Zo sluiten werkweek en maximum aan op
  // de kaart.
  const metAtv = invoer.atvEnabled ? invoer : invoerMetAtv(invoer)
  // De eigen werkweek met en zonder ATV (bv. 40 en 38; parttime 32 en 32). Het
  // voorbeeld noemt beide alleen als ze verschillen.
  const metAtvWeek = werkweekUren(metAtv)
  const zonderAtvWeek = werkweekUren(zonderAtv)
  return {
    contracturen: formatSpaarUren(invoer.contractUren),
    werkweekZonderAtv: formatSpaarUren(zonderAtvWeek),
    voorbeeldWerkweek:
      metAtvWeek === zonderAtvWeek
        ? `je werkweek van ${formatSpaarUren(metAtvWeek)} uur`
        : `je werkweek (met ATV ${formatSpaarUren(metAtvWeek)} uur, zonder ATV ${formatSpaarUren(zonderAtvWeek)} uur)`,
    // Waar overuren beginnen. Levert ATV bij jou nu niets op (ATV aan zonder
    // opbouw), dan is elk uur boven de eigen werkweek een overuur; anders zijn
    // met ATV de ATV-uren geen overuren.
    overurenGrens: nuZonderOpbouw
      ? `Bij jou levert ATV nu geen vrije dagen op: elk uur boven je werkweek van ${formatSpaarUren(werkweekUren(invoer))} uur is een overuur.`
      : `Met ATV zijn de uren tussen ${formatSpaarUren(atvBron.contractUren)} en ${formatSpaarUren(werkweekUren(atvBron))} uur geen overuren: die gaan al naar je ATV-pot.`,
    // Hoeveel werkweken het maximum is (beleid `tvt*MaxWeken`, default 1).
    maxSparenOmvang: maxSparenOmvang(invoer.beleid),
    // Waar het maximum op slaat, uit dezelfde tak als het getal (`maxSparenPerPot`):
    // bouwt ATV op, dan de werkweek met ATV; anders de contracturen, en die zijn
    // "jouw werkweek" als de werkweek gelijk is aan de contracturen.
    maxSparenBasis:
      atvSpaarUrenPerWeek(metAtv) > 0
        ? 'met ATV'
        : werkweekUren(metAtv) === metAtv.contractUren
          ? 'jouw werkweek'
          : 'je contracturen',
    atvContracturen: formatSpaarUren(atvBron.contractUren),
    atvWerkweekUren: formatSpaarUren(werkweekUren(atvBron)),
    atvUrenPerWeek: formatSpaarUren(atvSpaarUrenPerWeek(atvBron)),
    atvUrenPerJaar: formatSpaarUren(atvUrenPerJaar(atvBron)),
    atvDagenPerJaar: formatSpaarUren(atvDagenPerJaar(atvBron)),
    // Het eigen maximum met ATV aan (sparen kan alleen met ATV): 40 bij 38 uur
    // contract, anders de contracturen, bv. 32 bij een parttimer (FO-344 §4.3). Bij
    // ATV aan gelijk aan het maximum op de kaart; zonder ATV kan het afwijken (de
    // kaart hoort bij de huidige instelling).
    maxSparen: formatSpaarUren(maxSparenPerPot(metAtv, 'TVT_WERK')),
    maxSparenReistijd: formatSpaarUren(maxSparenPerPot(metAtv, 'TVT_REIS')),
    maxOverdracht: formatSpaarUren(maxOverdracht),
    vervaldatum: formatKalenderdatum(vervaldatum),
    vervalAtv: vervalUitlegAtv(vervaldatum),
    vervalTvt: vervalUitlegTvt(vervaldatum, maxOverdracht),
  }
}
